import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import Input from 'components/shared/form/Input';
import { parseDate, simpleDashDate, simpleDate } from 'lib/date';
import Workflow from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

interface Props {
  workflow: Workflow;
  onSave: (values: WorkflowValuesInput) => void;
  isSaving: boolean;
  // Terminal runs are frozen — values become read-only (design §11).
  readOnly: boolean;
}

const SOURCE_LABELS: Record<string, string> = {
  manual: 'Entered manually',
  extracted: 'Extracted',
  integration: 'From referral',
};

export default function ReferralPills({ workflow, onSave, isSaving, readOnly }: Props) {
  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState<Date | null>(null);

  const firstNameValue = workflow.valueFor('first_name');
  const lastNameValue = workflow.valueFor('last_name');
  const dobValue = workflow.valueFor('date_of_birth');

  const startEditing = () => {
    setFirstName(firstNameValue?.value ?? '');
    setLastName(lastNameValue?.value ?? '');
    setDob(parseDate(dobValue?.value) ?? null);
    setEditing(true);
  };

  const handleSave = () => {
    // Only send fields that changed, keyed by their declared (snake_case) names.
    const values: WorkflowValuesInput = {};
    if (firstName.trim() !== (firstNameValue?.value ?? '')) values.first_name = firstName.trim();
    if (lastName.trim() !== (lastNameValue?.value ?? '')) values.last_name = lastName.trim();
    const dobString = simpleDashDate(dob) ?? '';
    if (dobString !== (dobValue?.value ?? '')) values.date_of_birth = dobString;

    if (Object.keys(values).length > 0) onSave(values);
    setEditing(false);
  };

  if (editing) {
    return (
      <Panel data-testid='referral-pills-edit'>
        <Fields>
          <Field>
            <FieldLabel>First name</FieldLabel>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              data-testid='pill-input-first_name'
            />
          </Field>
          <Field>
            <FieldLabel>Last name</FieldLabel>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid='pill-input-last_name' />
          </Field>
          <Field>
            <FieldLabel>Date of birth</FieldLabel>
            <DatePicker selected={dob} onChange={setDob} $fullWidth placeholder='MM/DD/YYYY' />
          </Field>
        </Fields>
        <Actions>
          <Button size='small' onClick={handleSave} loading={isSaving} data-testid='pill-save'>
            Save demographics
          </Button>
          <Button variant='ghost' size='small' onClick={() => setEditing(false)} disabled={isSaving}>
            Cancel
          </Button>
        </Actions>
      </Panel>
    );
  }

  return (
    <Panel data-testid='referral-pills'>
      <PillRow>
        <Pill label='First name' value={firstNameValue?.value} source={firstNameValue?.source} />
        <Pill label='Last name' value={lastNameValue?.value} source={lastNameValue?.source} />
        <Pill label='Date of birth' value={simpleDate(dobValue?.value)} source={dobValue?.source} />
      </PillRow>
      {!readOnly && (
        <Button variant='outline-primary' size='small' onClick={startEditing} data-testid='pill-edit'>
          Edit demographics
        </Button>
      )}
    </Panel>
  );
}

function Pill({ label, value, source }: { label: string; value?: string | null; source?: string }) {
  return (
    <PillBox data-testid='pill'>
      <PillLabel>{label}</PillLabel>
      <PillValue $empty={!value}>{value || 'Not provided'}</PillValue>
      {value && source && <PillSource>{SOURCE_LABELS[source] ?? source}</PillSource>}
    </PillBox>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`;

const PillRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const PillBox = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  padding: 8px 16px;
  min-width: 140px;
  background: ${colors.white};
`;

const PillLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 11px;
`;

const PillValue = styled(BodySmallBold)<{ $empty: boolean }>`
  color: ${({ $empty }) => ($empty ? colors.black25 : colors.black)};
`;

const PillSource = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 10px;
  color: ${colors.black50};
`;

const Fields = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const Field = styled.div`
  min-width: 180px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;
